import { Input, Modal, message, Pagination, Select, Tag } from 'antd';
import { Check, Pencil, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { vmAgentPackageReviewApi } from '@/services/vmAgentPackageReviewApi';
import type {
  VMAgentPackageDataSource,
  VMAgentPackageReview,
  VMAgentPackageReviewItem,
  VMAgentPackageReviewStatus,
} from '@/types';
import {
  buildVMAgentPackageCompatibilityUpdateRequest,
  isCompatibilityRevisionConflict,
  validateVMAgentPackageCompatibilityForm,
} from './compatibility';

const pageSize = 50;

const statusOptions: Array<{
  label: string;
  value: VMAgentPackageReviewStatus | 'all';
}> = [
  { label: '全部', value: 'all' },
  { label: '待审核', value: 'pending' },
  { label: '已通过', value: 'approved' },
  { label: '未通过', value: 'rejected' },
];

function formatDateTime(value?: string) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString('zh-CN');
}

function minimumVersionLabel(value?: string) {
  return value?.trim() || '不限制';
}

function statusTag(status: VMAgentPackageReviewStatus) {
  if (status === 'approved') {
    return <Tag color="green">已通过</Tag>;
  }
  if (status === 'rejected') {
    return <Tag color="red">未通过</Tag>;
  }
  return <Tag color="gold">待审核</Tag>;
}

function toItem(row: VMAgentPackageReview): VMAgentPackageReviewItem {
  return {
    ...row,
    id: `${row.agent_id}:${row.version}`,
  };
}

export function VMAgentPackageReviewsPage({
  source = 'tutti-v2',
}: {
  source?: VMAgentPackageDataSource;
}) {
  const [items, setItems] = useState<VMAgentPackageReviewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [agentId, setAgentId] = useState('');
  const [status, setStatus] = useState<VMAgentPackageReviewStatus | 'all'>(
    'pending',
  );
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewTarget, setReviewTarget] =
    useState<VMAgentPackageReviewItem | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'rejected'>(
    'approved',
  );
  const [reviewNote, setReviewNote] = useState('');
  const [compatibilityTarget, setCompatibilityTarget] =
    useState<VMAgentPackageReviewItem | null>(null);
  const [minimumStableClientVersion, setMinimumStableClientVersion] =
    useState('');
  const [minimumRCClientVersion, setMinimumRCClientVersion] = useState('');
  const [compatibilityUpdateNote, setCompatibilityUpdateNote] = useState('');
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await vmAgentPackageReviewApi.list(
        {
          agentId: agentId.trim() || undefined,
          status,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
        source,
      );
      setItems(response.items.map((item) => toItem(item)));
      setTotal(response.total || 0);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [agentId, page, source, status]);

  useEffect(() => {
    void fetchReviews();
  }, [fetchReviews]);

  const openReview = useCallback(
    (target: VMAgentPackageReviewItem, nextStatus: 'approved' | 'rejected') => {
      setReviewTarget(target);
      setReviewStatus(nextStatus);
      setReviewNote(target.review_note || '');
    },
    [],
  );

  const openCompatibility = useCallback((target: VMAgentPackageReviewItem) => {
    setCompatibilityTarget(target);
    setMinimumStableClientVersion(target.minimum_stable_client_version || '');
    setMinimumRCClientVersion(target.minimum_rc_client_version || '');
    setCompatibilityUpdateNote('');
  }, []);

  const closeCompatibility = useCallback(() => {
    setCompatibilityTarget(null);
    setMinimumStableClientVersion('');
    setMinimumRCClientVersion('');
    setCompatibilityUpdateNote('');
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'agent',
        header: 'Agent',
        accessor: (row: VMAgentPackageReviewItem) => row.agent_id,
      },
      {
        id: 'version',
        header: 'Version',
        accessor: (row: VMAgentPackageReviewItem) => (
          <span className="font-mono">{row.version}</span>
        ),
      },
      {
        id: 'status',
        header: '状态',
        accessor: (row: VMAgentPackageReviewItem) => statusTag(row.status),
      },
      {
        id: 'minimumStableClientVersion',
        header: 'Stable 最低版本',
        accessor: (row: VMAgentPackageReviewItem) => (
          <span className="font-mono">
            {minimumVersionLabel(row.minimum_stable_client_version)}
          </span>
        ),
      },
      {
        id: 'minimumRCClientVersion',
        header: 'RC 最低版本',
        accessor: (row: VMAgentPackageReviewItem) => (
          <span className="font-mono">
            {minimumVersionLabel(row.minimum_rc_client_version)}
          </span>
        ),
      },
      {
        id: 'compatibilityAudit',
        header: '兼容性审计',
        accessor: (row: VMAgentPackageReviewItem) => (
          <div className="max-w-64 space-y-1 text-sm">
            <div>Revision {row.compatibility_revision}</div>
            <div>{row.compatibility_updated_by || '尚未修改'}</div>
            <div className="text-xs text-muted-foreground">
              {formatDateTime(row.compatibility_updated_at)}
            </div>
            <div
              className="truncate text-xs text-muted-foreground"
              title={row.compatibility_update_note}
            >
              {row.compatibility_update_note || '—'}
            </div>
          </div>
        ),
      },
      {
        id: 'firstPublishedAt',
        header: '首次发布时间',
        accessor: (row: VMAgentPackageReviewItem) =>
          formatDateTime(row.first_published_at),
      },
      {
        id: 'review',
        header: '审核信息',
        accessor: (row: VMAgentPackageReviewItem) => (
          <div className="space-y-1 text-sm">
            <div>{row.reviewed_by || '—'}</div>
            <div className="text-xs text-muted-foreground">
              {formatDateTime(row.reviewed_at)}
            </div>
            <div className="text-xs text-muted-foreground">
              {row.review_source || '—'}
            </div>
          </div>
        ),
      },
      {
        id: 'artifacts',
        header: 'Artifact',
        accessor: (row: VMAgentPackageReviewItem) => (
          <div className="space-y-1">
            {(row.artifacts || []).slice(0, 3).map((artifact) => (
              <div
                className="max-w-[360px] truncate font-mono text-xs"
                key={`${artifact.arch}:${artifact.file_name}`}
                title={artifact.url}
              >
                {artifact.arch} · {artifact.file_name}
              </div>
            ))}
            {(row.artifacts || []).length === 0 ? '—' : null}
          </div>
        ),
      },
      {
        id: 'note',
        header: '备注',
        accessor: (row: VMAgentPackageReviewItem) => row.review_note || '—',
      },
      {
        id: 'actions',
        header: '操作',
        accessor: (row: VMAgentPackageReviewItem) => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => openReview(row, 'approved')}
            >
              <Check className="mr-1 h-3.5 w-3.5" />
              通过
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openReview(row, 'rejected')}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              不通过
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openCompatibility(row)}
            >
              <Pencil className="mr-1 h-3.5 w-3.5" />
              兼容性
            </Button>
          </div>
        ),
      },
    ],
    [openCompatibility, openReview],
  );

  const submitReview = async () => {
    if (!reviewTarget) {
      return;
    }
    setActionLoading(true);
    try {
      await vmAgentPackageReviewApi.update(
        {
          agent_id: reviewTarget.agent_id,
          version: reviewTarget.version,
          status: reviewStatus,
          review_note: reviewNote.trim(),
        },
        source,
      );
      message.success(reviewStatus === 'approved' ? '已通过' : '已标记不通过');
      setReviewTarget(null);
      setReviewNote('');
      await fetchReviews();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const submitCompatibility = async () => {
    if (!compatibilityTarget) {
      return;
    }
    const form = {
      minimumStableClientVersion,
      minimumRCClientVersion,
      updateNote: compatibilityUpdateNote,
    };
    const validationError = validateVMAgentPackageCompatibilityForm(form);
    if (validationError) {
      message.error(validationError);
      return;
    }

    setCompatibilityLoading(true);
    try {
      await vmAgentPackageReviewApi.updateCompatibility(
        buildVMAgentPackageCompatibilityUpdateRequest(
          compatibilityTarget.agent_id,
          compatibilityTarget.version,
          compatibilityTarget.compatibility_revision,
          form,
        ),
        source,
      );
      message.success('兼容性限制已更新');
      closeCompatibility();
      await fetchReviews();
    } catch (error) {
      if (isCompatibilityRevisionConflict(error)) {
        closeCompatibility();
        await fetchReviews();
        message.warning('数据已被其他管理员更新，请基于最新数据重新编辑');
      } else {
        message.error(error instanceof Error ? error.message : '操作失败');
      }
    } finally {
      setCompatibilityLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-60 space-y-2">
            <Label htmlFor="vm-agent-review-agent">Agent</Label>
            <Input
              id="vm-agent-review-agent"
              value={agentId}
              onChange={(event) => {
                setAgentId(event.target.value);
                setPage(1);
              }}
              placeholder="codex"
            />
          </div>
          <div className="min-w-44 space-y-2">
            <Label htmlFor="vm-agent-review-status">状态</Label>
            <Select
              id="vm-agent-review-status"
              className="w-full"
              value={status}
              options={statusOptions}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
            />
          </div>
          <Button onClick={() => void fetchReviews()} disabled={loading}>
            刷新
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h1 className="text-lg font-semibold text-foreground">
            Agent 包审核
          </h1>
          <span className="text-sm text-muted-foreground">共 {total} 条</span>
        </div>
        <DataTable
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="暂无 Agent 包审核记录"
        />
        <div className="mt-4 flex justify-end">
          <Pagination
            current={page}
            total={total}
            pageSize={pageSize}
            showSizeChanger={false}
            onChange={(nextPage) => setPage(nextPage)}
          />
        </div>
      </div>

      <Modal
        title={reviewStatus === 'approved' ? '通过审核' : '审核不通过'}
        open={reviewTarget !== null}
        confirmLoading={actionLoading}
        onOk={() => void submitReview()}
        onCancel={() => setReviewTarget(null)}
        okText="确认"
        cancelText="取消"
      >
        <div className="space-y-4">
          <div className="rounded-md border border-border bg-slate-50 p-3 text-sm">
            <div>Agent：{reviewTarget?.agent_id}</div>
            <div>Version：{reviewTarget?.version}</div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vm-agent-review-note">备注</Label>
            <Input.TextArea
              id="vm-agent-review-note"
              value={reviewNote}
              rows={4}
              onChange={(event) => setReviewNote(event.target.value)}
              placeholder="填写审核备注"
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="编辑客户端兼容性"
        open={compatibilityTarget !== null}
        confirmLoading={compatibilityLoading}
        onOk={() => void submitCompatibility()}
        onCancel={closeCompatibility}
        okText="保存"
        cancelText="取消"
      >
        <div className="space-y-4">
          <div className="rounded-md border border-border bg-slate-50 p-3 text-sm">
            <div>Agent：{compatibilityTarget?.agent_id}</div>
            <div>Version：{compatibilityTarget?.version}</div>
            <div>
              Compatibility Revision：
              {compatibilityTarget?.compatibility_revision}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vm-agent-minimum-stable-client-version">
              Stable 最低客户端版本
            </Label>
            <Input
              id="vm-agent-minimum-stable-client-version"
              value={minimumStableClientVersion}
              onChange={(event) =>
                setMinimumStableClientVersion(event.target.value)
              }
              placeholder="不限制；或输入 x.y.z"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vm-agent-minimum-rc-client-version">
              RC 最低客户端版本
            </Label>
            <Input
              id="vm-agent-minimum-rc-client-version"
              value={minimumRCClientVersion}
              onChange={(event) =>
                setMinimumRCClientVersion(event.target.value)
              }
              placeholder="不限制；或输入 x.y.z-rc.n"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vm-agent-compatibility-update-note">
              修改原因（必填）
            </Label>
            <Input.TextArea
              id="vm-agent-compatibility-update-note"
              value={compatibilityUpdateNote}
              rows={4}
              onChange={(event) =>
                setCompatibilityUpdateNote(event.target.value)
              }
              placeholder="说明本次调整原因"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            清空对应版本表示该渠道不限制。保存后会立即参与选包和已安装包自愈判定。
          </p>
        </div>
      </Modal>
    </div>
  );
}
